import { ApiVersionFactoryModule } from '@b2c/common/factories/apiVersion/apiVersion.factory';
import { ThemesData } from '@b2c/common/resolvers/themes-data.resolver';
import { ACMEResponse } from '@b2c/types/core/response.type';
import { TODO } from '@b2c/types/index';
import angular, { IHttpResponse, IHttpService, IQService } from 'angular';

export class ThemesService {
  static $inject = ['$http', '$q', 'ApiVersionFactory'];

  api = {
    av_themes: new this.ApiVersionFactory({
      url: '/v1/b2c/themes/',
      version: 'v2',
    }),

    detailStyles: () => {
      return this.$http({
        method: 'get',
        url: this.api.av_themes.url,
      });
    },

    detail: (tenantId, urlParams) => {
      return this.$http<ACMEResponse<ThemesData>>({
        method: 'get',
        url: this.api.av_themes.url + tenantId + urlParams,
      });
    },

    update: (props) => {
      return this.$http({
        method: 'put',
        url: this.api.av_themes.url + props.tenantId,
        data: props.theme,
      });
    },
  };

  model: TODO = {};
  unsafe: TODO = {};

  constructor(private $http: IHttpService, private $q: IQService, private ApiVersionFactory: TODO) {}

  init(tenantId) {
    const q = this.$q.defer<IHttpResponse<ACMEResponse<ThemesData>>>();

    const params = new URLSearchParams(window.location.search);
    const overrideId = params.get('text');
    const urlParams = overrideId ? '?overrideId=' + overrideId : '';
    this.api
      .detail(tenantId, urlParams)
      .then((response) => {
        this.model = response.data.payload;
        q.resolve(response);
      })
      .catch((response) => {
        q.reject(response);
      });

    return q.promise;
  }

  getLinks() {
    const result: TODO[] = [];

    if (this.model.hasOwnProperty('links')) {
      for (let i = 0, ii = this.model.links.length; i < ii; i++) {
        if (this.model.links[i].key !== 'logo') {
          result.push(this.model.links[i]);
        } else {
          this.model.logo = this.model.links[i];
        }
      }
    }

    return result;
  }

  getLogo() {
    if (!this.model.logo && this.model.hasOwnProperty('links')) {
      for (let i = 0, ii = this.model.links.length; i < ii; i++) {
        if (this.model.links[i].key === 'logo') {
          this.model.logo = this.model.links[i];
          break;
        }
      }
    }

    return this.model.logo;
  }

  getTextVariable(key: string, fallback?: string): TODO {
    const textVariables = this.model.textVariables ?? {};
    return textVariables[key] || textVariables[`${key}-MARKUP`] || fallback || '';
  }

  getDynamicTextVariable(key: string, data: Record<string, unknown>, value?: string) {
    let r = value ? value : '';
    const keys = Object.keys(data)
      .map((item) => item.replace('$', '\\$'))
      .join('|');
    const replacePattern = new RegExp(keys, 'gi');

    if (this.model.hasOwnProperty('textVariables') && this.model.textVariables.hasOwnProperty(key)) {
      r = this.model.textVariables[key].replace(replacePattern, (matched) => {
        return data[matched];
      });
    }

    return r;
  }

  selectTextVariables<T extends Record<string, string | { defaultValue: string; params?: Record<string, string> }>>(
    props: T
  ) {
    const result = {} as Record<keyof T, string>;

    for (const key of Object.keys(props)) {
      const item =
        typeof props[key] === 'string'
          ? { defaultValue: props[key] }
          : (props[key] as { defaultValue: string; params?: Record<string, string> });

      (result as TODO)[key] = item.params
        ? this.getDynamicTextVariable(key, item.params ?? {}, item.defaultValue)
        : this.getTextVariable(key, item.defaultValue);
    }

    return result;
  }

  getEmailVariable(key, value) {
    let r = value ? value : '';

    if (this.model.hasOwnProperty('emailVariables') && this.model.emailVariables.hasOwnProperty(key)) {
      r = this.model.emailVariables[key];
    }

    return r;
  }

  getCSSVariables() {
    return this.model.variables ? this.model.variables : {};
  }

  getCSSVariable(key, value) {
    let r = value ? value : '';

    if (this.model.hasOwnProperty('variables') && this.model.variables.hasOwnProperty(key)) {
      r = this.model.variables[key];
    }

    return r;
  }

  getCompany(key) {
    let result: TODO = '';

    if (this.model.hasOwnProperty('company')) {
      result = this.model.company;
      if (key) {
        if (this.model.company.hasOwnProperty(key)) {
          result = this.model.company[key];
        } else {
          result = false;
        }
      }
    }

    return result;
  }

  isCompanyLoaded() {
    return this.model.hasOwnProperty('company');
  }

  getFeature(key) {
    let result: TODO = '';

    if (this.model.hasOwnProperty('featuresConfig')) {
      result = this.model.featuresConfig;
      if (key) {
        if (this.model.featuresConfig.hasOwnProperty(key)) {
          result = this.model.featuresConfig[key];
        } else {
          result = false;
        }
      }
    }

    return result;
  }

  getCCValidationSettings(key) {
    let result: TODO | false = '';

    if (this.model.hasOwnProperty('creditCardValidationSettings')) {
      result = this.model.creditCardValidationSettings;
      if (
        this.model.creditCardValidationSettings[key + 'Validation'] &&
        this.model.creditCardValidationSettings[key + 'Display']
      ) {
        result = {
          display: this.model.creditCardValidationSettings[key + 'Display'],
          validation: this.model.creditCardValidationSettings[key + 'Validation'],
        };
      } else {
        result = false;
      }
    }

    return result ? result : false;
  }

  getHours(key) {
    let result: string | false = '';

    if (this.model.hasOwnProperty('openHours')) {
      result = this.model.openHours;
      if (key) {
        if (this.model.openHours.hasOwnProperty(key)) {
          result = this.model.openHours[key];
        } else {
          result = false;
        }
      }
    }

    return result;
  }
}

export const ThemesServiceModule = angular
  .module('acme.common.service.themes', [ApiVersionFactoryModule])
  .service('ThemesService', ThemesService).name;
